<template>
  <div class="screen uitleg">
    <button v-if="step > 0" class="uitleg__prev" type="button" @click="prev">
      <svg width="20" height="14" viewBox="0 0 20 14" fill="none" aria-hidden="true">
        <path
          d="M19 7H2M8 1L2 7l6 6"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      Vorige stap
    </button>

    <div class="uitleg__body">
      <div class="uitleg__content" :class="{ 'uitleg__content--first': step === 0 }">
        <!-- step 1: how the game works -->
        <template v-if="step === 0">
          <h1 class="uitleg__title">Hoe werkt het Priority Spel?</h1>
          <ul class="uitleg__cards">
            <li v-for="c in cards" :key="c.n" class="ucard">
              <span class="ucard__num">{{ c.n }}</span>
              <p class="ucard__text">{{ c.text }}</p>
              <div class="ucard__art" :class="'ucard__art--' + c.art" aria-hidden="true">
                <template v-if="c.art === 'start'">
                  <span class="ucard__pill">Start</span>
                  <img class="ucard__hand" :src="handImg" alt="" />
                </template>
                <template v-else-if="c.art === 'plus-star'">
                  <span class="ucard__plus">+</span>
                  <img class="ucard__star" :src="starImg" alt="" />
                </template>
                <template v-else>
                  <img class="ucard__stararrow" :src="starArrowImg" alt="" />
                  <img class="ucard__gift" :src="giftImg" alt="" />
                </template>
              </div>
            </li>
          </ul>
        </template>

        <!-- step 2: turns per tier + when you can win -->
        <template v-else>
          <h1 class="uitleg__title">Hoe vaak kan ik spelen?</h1>
          <div class="ucard ucard--tier">
            <img class="ucard__tier" :src="tierIcon" alt="" />
            <p class="ucard__text">
              <strong>{{ tierIntro }}</strong> Daarom mag je {{ store.turnsTotal }}
              {{ turnsWord }} per week spelen.
            </p>
          </div>

          <h2 class="uitleg__title uitleg__title--sub">Wanneer kan ik prijzen winnen?</h2>
          <p class="uitleg__text">
            Elke 7 weken eindigt de speelronde en worden de prijswinnaars bekend gemaakt.
          </p>
        </template>
      </div>
    </div>

    <div class="screen__footer">
      <AppButton variant="cta" @click="next">Verder</AppButton>
      <div class="uitleg__dots" aria-hidden="true">
        <span :class="{ 'is-active': step === 0 }" />
        <span :class="{ 'is-active': step === 1 }" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useGameStore } from '@/stores/game'
import AppButton from '@/components/ui/AppButton.vue'
import starImg from '@/assets/v3/uitleg-star.svg'
import starArrowImg from '@/assets/v3/uitleg-star-arrow.svg'
import handImg from '@/assets/v3/uitleg-hand.webp'
import giftImg from '@/assets/v3/uitleg-gift.webp'
import tierZilver from '@/assets/v3/tier-zilver.webp'
import tierGoud from '@/assets/v3/tier-goud.webp'
import tierPlatinum from '@/assets/v3/tier-platinum.webp'
import tierDiamant from '@/assets/v3/tier-diamant.webp'

const store = useGameStore()
const router = useRouter()

// Figma 421:1205 — the tier badge icon (neon star) per Priority tier.
const TIER_ICONS = {
  zilver: tierZilver,
  goud: tierGoud,
  platinum: tierPlatinum,
  diamant: tierDiamant,
}
const tierIcon = computed(() => TIER_ICONS[store.server?.player?.tier] ?? tierDiamant)

// Tier-introzin. "Je bent een Diamant" houdt het lidwoord (idiomatisch: iemand 'is
// een diamant'); de overige tiers zijn een status en krijgen géén lidwoord
// ("Je bent Goud."). De punt hoort bij de zin, dus staat mee in de string.
const tierIntro = computed(() =>
  store.server?.player?.tier === 'diamant'
    ? `Je bent een ${store.tierLabel}.`
    : `Je bent ${store.tierLabel}.`,
)
// Enkelvoud bij 1 speelbeurt (Zilver), anders meervoud.
const turnsWord = computed(() => (store.turnsTotal === 1 ? 'spel' : 'spellen'))
const step = ref(0)
const LAST = 1

const cards = [
  { n: 1, text: 'Draai aan het spellenrad', art: 'start' },
  { n: 2, text: 'Speel het spel en verdien sterren', art: 'plus-star' },
  { n: 3, text: 'Zet je sterren in voor vette prijzen', art: 'star-gift' },
]

function next() {
  if (step.value < LAST) {
    step.value += 1
  } else {
    store.markIntroSeen()
    router.replace({ name: 'home' })
  }
}
function prev() {
  if (step.value > 0) step.value -= 1
}
</script>

<style lang="scss" scoped>
// -----------------------------------------------------------------------------
// Layout: the CTA + step dots are pinned to the bottom and the rest of the
// content is centred in whatever space is left over:
//
//   .uitleg__prev     absolute white tab, top-left (step 2 only)
//   .uitleg__body     flex: 1 1 auto, min-height: 0  -> the leftover space
//     .uitleg__content  margin-block: auto           -> centred inside it
//   .screen__footer   flex: none                     -> always at the bottom
//
// `margin-block: auto` rather than `justify-content: center` on purpose: when
// the content is taller than the space (a very short viewport) auto margins
// resolve to 0, so the content starts at the top and every line stays reachable
// by scrolling .uitleg__body - centring would cut the top off instead.
//
// The body reserves the 60px "Vorige stap" tab on BOTH steps: it keeps the two
// steps centred in the same box, so nothing jumps when you page between them,
// and the tab can never land on the content.
//
// Everything with slack (side insets, vertical gaps, the CTA) is a clamp
// between a small-screen floor and the Figma value, so the 390x771 artboard
// renders as drawn and smaller screens give way gracefully instead of
// overflowing. Figma: step 1 = 421:1099, step 2 = 421:1167.
// -----------------------------------------------------------------------------
.uitleg {
  // Bound the column to the viewport (.screen only sets a min-height) so that a
  // screen too small for the content scrolls INSIDE .uitleg__body and the CTA
  // stays where it is, instead of the whole page growing and pushing it away.
  height: 100vh;
  height: 100dvh;

  // How much of its Figma size the card art gets. Full size from 390px up; on a
  // narrower column it shrinks so the text column keeps enough room to wrap into
  // two lines instead of four (which is what pushed card 3 off the screen).
  --art: clamp(0.72px, (100vw - 120px) / 270, 1px);

  background: #f1f1f1;
  color: var(--c-ink);
}

.uitleg__prev {
  // White tab flush to the top edge (the header was removed), rounded bottom-right only
  position: absolute;
  top: env(safe-area-inset-top);
  left: 0;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  height: 60px;
  padding: 0 24px 0 22px;
  font-family: var(--font-brand);
  font-size: var(--fs-lg);
  font-weight: var(--fw-regular);
  color: var(--c-ink);
  background: var(--c-white);
  border: none;
  border-radius: 0 0 23px 0;
}

.uitleg__body {
  flex: 1 1 auto;
  min-height: 0; // give way before the footer does
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: calc(60px + env(safe-area-inset-top) + var(--sp-4)) 0 var(--sp-5);
}

.uitleg__content {
  margin-block: auto; // safe centring - see the note above
  width: 100%;
  padding-inline: max(20px, 10%); // Figma 421:1203 - tier card at x=39 of 390
}
// Figma 421:1128 - step-1 cards run x=46..358; the number badge hangs 20px to
// the left of that, so its own edge lands on 26.
.uitleg__content--first {
  padding-left: max(24px, 11.79%);
  padding-right: max(16px, 8.21%);
}

.uitleg__title {
  font-size: clamp(19px, 5.64vw, var(--fs-2xl)); // Figma: 23px, centered
  font-weight: var(--fw-bold);
  text-align: center;
  color: var(--c-ink);

  &--sub {
    margin-top: clamp(24px, 5.19dvh, 40px);
  }
}
.uitleg__text {
  margin-top: var(--sp-3);
  font-size: var(--fs-lg); // Figma 421:1206 — 18px, centered
  line-height: 1.28;
  text-align: center;
  color: var(--c-ink);
}

.uitleg__cards {
  list-style: none;
  margin: clamp(16px, 3.89dvh, 30px) 0 0; // Figma — title (182) → first card (239)
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 1.95dvh, 15px); // Figma — card 1 ends 332, card 2 starts 347
}

.ucard {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  min-height: clamp(76px, 11.93dvh, 92px); // Figma 421:1128 — 92px
  padding: var(--sp-4) var(--sp-4) var(--sp-4) 56px;
  background: var(--c-white);
  // Figma 421:1128 — 1px hairline border, no shadow
  border: 1px solid var(--c-border);
  border-radius: 15px;
}
// numbered cards: the badge hangs off the left edge, so the text starts ~32px
// in from the card border (Figma 421:1129 — text at x=78, card edge at x=46).
// The 32px on the right is Figma's Start pill at x=325 in a card ending at 358,
// which is what keeps the (deliberately overflowing) hand inside the card.
.uitleg__cards .ucard {
  padding-inline: clamp(18px, 8.21vw, 32px);
}
// Match Figma's fixed text column (114/138/145px) so lines wrap the same way,
// and keep the card art pinned to the right edge.
.uitleg__cards .ucard__text {
  flex: 0 1 auto;
  max-width: 150px;
}
.uitleg__cards .ucard__art {
  margin-left: auto;
}
.ucard__num {
  position: absolute;
  left: -20px; // Figma 421:1130 — badge center straddles the card's left edge
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: var(--fs-lg);
  font-weight: var(--fw-extrabold);
  color: var(--c-white);
  background: var(--c-blue-deep);
  border-radius: 50%;
}
.ucard__text {
  flex: 1 1 auto;
  // 18px per Figma 421:1129; eases off on a narrow column so the card text
  // still wraps into two lines next to the art instead of three or four
  font-size: clamp(15px, 4.7vw, var(--fs-lg));
  font-weight: var(--fw-regular);
  line-height: 1.3;
  color: var(--c-ink);

  strong {
    font-weight: var(--fw-bold);
  }
}
// Figma 421:1203 — tier card: 18px radius, icon inset ~13px, ~16px gap to text
.ucard--tier {
  margin-top: clamp(12px, 2.59dvh, 20px);
  gap: 16px;
  padding-block: 13px; // Figma — 65px icon in a 92px card
  padding-left: 13px;
  border-radius: 18px;
}
.ucard--tier .ucard__text {
  max-width: 196px; // Figma 421:1204 — text column so it wraps to 3 lines
  font-size: var(--fs-base); // 16px
  font-weight: var(--fw-regular);
}
.ucard__art {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
// Card art — Figma 421:1132 (start+hand) / 421:1139 (+ star) / 421:1144 (star→gift).
// Sizes and the 3D drop shadow (0 4px 0 rgba(0,0,0,.25)) come straight off those
// nodes; on the emoji PNGs it has to be a `filter` so the shadow follows the
// artwork's alpha instead of its box.
// The art is right-aligned against the card's 32px padding, which puts the Start
// pill exactly where Figma has it (x=325 in a card ending at 358). The other two
// groups sit 4px further left / 8px further right in the design, so they get the
// difference as a margin rather than three different card paddings.
.ucard__art--start {
  position: relative; // the hand is placed against the pill
}
.ucard__art--plus-star {
  margin-right: calc(2 * var(--art)); // Figma 421:1141 — the star ink ends at x=321
}
.ucard__art--star-gift {
  margin-right: calc(-8 * var(--art)); // Figma 421:1156 — gift ends at x=334
}
.ucard__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: calc(82 * var(--art)); // Figma 421:1134 — 82×42
  height: calc(42 * var(--art));
  font-family: var(--font-brand);
  font-size: calc(17 * var(--art)); // Figma 421:1135
  font-weight: var(--fw-bold);
  color: var(--c-white);
  background: var(--c-blue-deep);
  border: 3px solid var(--c-white);
  border-radius: 12px;
  box-shadow: 0 4px 0 0 rgba(0, 0, 0, 0.25);
}
.ucard__hand {
  // Figma 421:1136 — a 46.93px square rotated 60°, its centre 76.05/35.05 from
  // the pill's top-left, so it overlaps the pill's bottom-right corner. The
  // filter runs before the transform, so the shadow rotates with the hand.
  position: absolute;
  left: calc(76.05 * var(--art));
  top: calc(35.05 * var(--art));
  width: calc(46.93 * var(--art));
  height: calc(46.93 * var(--art));
  transform: translate(-50%, -50%) rotate(60deg);
  filter: drop-shadow(0 4px 0 rgba(0, 0, 0, 0.25));
}
.ucard__plus {
  font-family: var(--font-brand);
  font-size: calc(40 * var(--art)); // Figma 421:1140
  font-weight: var(--fw-regular);
  line-height: 1;
  color: var(--c-black);
}
// Both SVGs carry a transparent margin around the artwork, so the box is set so
// that the INK matches Figma: the star renders 52×49 (421:1141) and the
// star→arrow 46×25 (421:1145), measured on the rendered frame.
.ucard__star {
  width: calc(56 * var(--art));
  height: calc(53 * var(--art));
}
.ucard__stararrow {
  width: calc(49 * var(--art));
  height: calc(27 * var(--art));
}
.ucard__gift {
  width: calc(45 * var(--art)); // Figma 421:1156
  height: calc(45 * var(--art));
  filter: drop-shadow(0 4px 0 rgba(0, 0, 0, 0.25));
}
.ucard__tier {
  // Figma 421:1205 — tier badge image (66×65, 11px radius)
  flex: none;
  width: 66px;
  height: 65px;
  border-radius: 11px;
  object-fit: cover;
}

.uitleg :deep(.btn--cta) {
  // Figma 421:1122/1196 — 69px tall, 23px label
  height: clamp(56px, 8.95dvh, 69px);
  font-size: var(--fs-2xl);
}
.uitleg .screen__footer {
  // Figma — CTA 342 wide at x=24 on the 390 artboard
  flex: none;
  padding: 0 max(16px, 6.15%) max(var(--sp-5), min(56px, 7.26dvh));
}
.uitleg__dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: clamp(20px, 5.84dvh, 45px); // Figma — button bottom (665) → dots (710)

  span {
    width: 34px;
    height: 5px;
    border-radius: 3px;
    background: #c9c9c9;
  }
  span.is-active {
    background: var(--c-ink);
  }
}
</style>
